import React, { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
    FormField,
    FormItem,
    FormControl,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { HelpCircle, RotateCcw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { TERTIARY_PACKAGE_TYPE_OPTIONS } from '@/lib/packageOptions';

interface TertiaryPackageDetailsProps {
    form: UseFormReturn<any>;
    request?: {
        items_per_case?: string;
        pack_per_case?: string;
    };
}

// const materials = [
//     "RSC",
//     "HSC",
//     "Tray",
//     "Wrap",
//     "Shrink",
//     "Other"
// ];
const materials = TERTIARY_PACKAGE_TYPE_OPTIONS;
const lbsUnits = ["lbs", "oz", 'g', 'kg']
const inUnits = ["inch", "mm"]

const packageTypeFactor = {
    "Shell": { length: 1.6, width: 0.94, height: 0.62 },
    "Case": { length: 0.49, width: 0.52, height: 0.36 },
    "Corrugated": { length: 0.49, width: 0.52, height: 0.36 },
    "Corrugated Case": { length: 0.49, width: 0.52, height: 0.36 },
    "Paperboard Wrap": { length: 0.25, width: 0.09, height: 0.05 },
    "Paperboard": { length: 0.25, width: 0.09, height: 0.05 },
    "Shrink": { length: -0.28, width: -0.17, height: 0.29 },
    "Shrink Bundle": { length: -0.28, width: -0.17, height: 0.29 },
    "Shrink W Pad": { length: -0.31, width: -0.2, height: 0.87 },
    "Shrink W Tray": { length: -0.05, width: 0.06, height: 0.61 },
    "Tray -Shrink": { length: -0.05, width: 0.06, height: 0.61 },
    "TRAY & CASE": { length: 0.28, width: 0.17, height: 0.5 },
    "Tray": { length: 0.1, width: 0.1, height: 0.54 },
    "Wrap": { length: -2.2, width: 1.5, height: 0.3 },
    "PAD": { length: -0.35, width: -0.28, height: 0.66 },
    "case/pack": { length: 0.29, width: 0.15, height: 0.22 },
    "Base": { length: 0.8, width: 0.8, height: 0.3 },
    "Others": { length: 0.8, width: 0.8, height: 0.3 },
    "CASE/Shell": { length: 1.6, width: 0.9, height: 0.3 }
};

const weightToPounds = (value: unknown, unit: string): number => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 0;

    switch (unit) {
        case 'oz':
            return numericValue / 16;
        case 'g':
            return numericValue * 0.00220462;
        case 'kg':
            return numericValue * 2.20462;
        case 'lbs':
        default:
            return numericValue;
    }
};

const extractPackPerCase = (packPerCase: unknown): number => {
    const match = String(packPerCase ?? '').trim().match(/^\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : 0;
};

export const calculateTertiaryPackageWeight = (
    secondaryWeight: unknown,
    secondaryWeightUnit: string = 'lbs',
    packPerCase: unknown
): string => {
    const secondaryWeightLbs = weightToPounds(secondaryWeight, secondaryWeightUnit);
    const packCount = extractPackPerCase(packPerCase);

    if (!secondaryWeightLbs || !packCount) return '';

    return (secondaryWeightLbs * packCount + 0.22).toFixed(2);
};

const TertiaryPackageDetails: React.FC<TertiaryPackageDetailsProps> = ({ form, request }) => {
    const [isLengthAutoCalculated, setIsLengthAutoCalculated] = useState(true);
    const [isWidthAutoCalculated, setIsWidthAutoCalculated] = useState(true);
    const [isHeightAutoCalculated, setIsHeightAutoCalculated] = useState(true);

    const convertLength = (value: number, fromUnit: string, toUnit: string): number => {
        // Convert to inches first (1 mm = 0.0393701 inches)
        const valueInInches = fromUnit === 'mm' ? value * 0.0393701 : value;
        // Then convert to target unit
        return toUnit === 'mm' ? valueInInches / 0.0393701 : valueInInches;
    };

    const safeNumberConvert = (value: any): number => {
        if (value === undefined || value === null || value === '') return 0;
        const num = typeof value === 'string' ? Number(value) : value;
        return isNaN(num) ? 0 : num;
    };

    const safeToFixed = (value: any, decimals: number = 2): string => {
        const num = safeNumberConvert(value);
        return num.toFixed(decimals);
    };

    const calculateTertiaryDimension = (dimension: 'length' | 'width' | 'height'): number => {
        const tertiaryPackageType = form.getValues('tertiary_material');
        const secondaryLength = safeNumberConvert(form.getValues('secondary_length'));
        const secondaryWidth = safeNumberConvert(form.getValues('secondary_width'));
        const secondaryHeight = safeNumberConvert(form.getValues('secondary_height'));
        const secondaryLengthUnit = form.getValues('secondary_length_unit') || 'inch';
        const secondaryWidthUnit = form.getValues('secondary_width_unit') || 'inch';
        const secondaryHeightUnit = form.getValues('secondary_height_unit') || 'inch';
        const tertiarySecondaryPackagesPerUnit = request?.pack_per_case;

        console.log('Calculation inputs:', {
            tertiaryPackageType,
            secondaryLength,
            secondaryWidth,
            secondaryHeight,
            secondaryLengthUnit,
            secondaryWidthUnit,
            secondaryHeightUnit,
            tertiarySecondaryPackagesPerUnit
        });

        if (!tertiaryPackageType || !secondaryLength || !secondaryWidth || !secondaryHeight || !tertiarySecondaryPackagesPerUnit) {
            console.warn('Missing required inputs for calculation');
            return 0;
        }

        const factor = packageTypeFactor[tertiaryPackageType as keyof typeof packageTypeFactor];
        console.log('Package type factor:', factor);

        if (!factor) {
            console.warn('No factor found for package type');
            return 0;
        }

        const [totalItems, layout] = tertiarySecondaryPackagesPerUnit.split(' ');
        const [rows, columns] = layout.slice(1, -1).split(/[x×]/i).map(safeNumberConvert);
        console.log('Layout details:', { totalItems, rows, columns });

        // Convert dimensions to inches if in mm
        const lengthInInches = secondaryLengthUnit === 'mm' ? secondaryLength / 25.4 : secondaryLength;
        const widthInInches = secondaryWidthUnit === 'mm' ? secondaryWidth / 25.4 : secondaryWidth;
        const heightInInches = secondaryHeightUnit === 'mm' ? secondaryHeight / 25.4 : secondaryHeight;

        console.log('Converted dimensions:', {
            lengthInInches,
            widthInInches,
            heightInInches
        });

        switch (dimension) {
            case 'length':
                const lengthCalc = rows * lengthInInches + factor.length;
                console.log('Length calculation:', `${rows} * ${lengthInInches} + ${factor.length} = ${lengthCalc}`);
                return lengthCalc;
            case 'width':
                const widthCalc = columns * widthInInches + factor.width;
                console.log('Width calculation:', `${columns} * ${widthInInches} + ${factor.width} = ${widthCalc}`);
                return widthCalc;
            case 'height':
                const heightCalc = heightInInches + factor.height;
                console.log('Height calculation:', `${heightInInches} + ${factor.height} = ${heightCalc}`);
                return heightCalc;
            default:
                return 0;
        }
    };

    // Add useEffect to auto-calculate on page load and when dependencies change
    useEffect(() => {
        if (form.getValues('isTertiaryLengthAutoCalculated')) {
            const calculatedLength = calculateTertiaryDimension('length');
            const unit = form.getValues('tertiary_length_unit') || 'in';
            const convertedLength = convertLength(calculatedLength, 'in', unit);
            form.setValue('tertiary_length', safeToFixed(convertedLength), {
                shouldValidate: true,
                shouldDirty: true
            });
            console.log('Auto-calculated length:', convertedLength);
        }
    }, [
        form.getValues('tertiary_material'),
        form.getValues('secondary_length'),
        form.getValues('secondary_length_unit'),
        request?.pack_per_case,
        form.getValues('isTertiaryLengthAutoCalculated')
    ]);

    useEffect(() => {
        if (form.getValues('isTertiaryWidthAutoCalculated')) {
            const calculatedWidth = calculateTertiaryDimension('width');
            const unit = form.getValues('tertiary_width_unit') || 'in';
            const convertedWidth = convertLength(calculatedWidth, 'in', unit);
            form.setValue('tertiary_width', safeToFixed(convertedWidth), {
                shouldValidate: true,
                shouldDirty: true
            });
            console.log('Auto-calculated width:', convertedWidth);
        }
    }, [
        form.getValues('tertiary_material'),
        form.getValues('secondary_width'),
        form.getValues('secondary_width_unit'),
        request?.pack_per_case,
        form.getValues('isTertiaryWidthAutoCalculated')
    ]);

    useEffect(() => {
        if (form.getValues('isTertiaryHeightAutoCalculated')) {
            const calculatedHeight = calculateTertiaryDimension('height');
            const unit = form.getValues('tertiary_height_unit') || 'in';
            const convertedHeight = convertLength(calculatedHeight, 'in', unit);
            form.setValue('tertiary_height', safeToFixed(convertedHeight), {
                shouldValidate: true,
                shouldDirty: true
            });
            console.log('Auto-calculated height:', convertedHeight);
        }
    }, [
        form.getValues('tertiary_material'),
        form.getValues('secondary_height'),
        form.getValues('secondary_height_unit'),
        form.getValues('isTertiaryHeightAutoCalculated')
    ]);

    useEffect(() => {
        if (form.getValues('isTertiaryWeightAutoCalculated')) {
            const calculatedWeight = calculateTertiaryPackageWeight(
                form.getValues('secondary_weight'),
                form.getValues('secondary_weight_unit') || 'lbs',
                request?.pack_per_case
            );

            if (calculatedWeight) {
                form.setValue('tertiary_weight', calculatedWeight, {
                    shouldValidate: true,
                    shouldDirty: true
                });
                form.setValue('tertiary_weight_unit', 'lbs');
            }
        }
    }, [
        form.getValues('secondary_weight'),
        form.getValues('secondary_weight_unit'),
        request?.pack_per_case,
        form.getValues('isTertiaryWeightAutoCalculated')
    ]);

    return (
        <div>
            {/* Header Row */}
            <div className="flex-center border-border-style-1 border w-full">
                <div className="label-bg">
                    <Label className="label font-bold">Tertiary Package Details</Label>
                </div>
            </div>

            {/* First Row */}
            <div className="flex w-full border-x border-b">
                <div className="flex-1 p-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                            <Label className="label font-bold">Tertiary Package Type*</Label>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Select the tertiary package type</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <FormField
                            control={form.control}
                            name="tertiary_material"
                            render={({ field }) => (
                                <FormItem>
                                    <Select
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="bg-selectBlue border-required border-2">
                                                <SelectValue placeholder="Select tertiary package type" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {materials.map((material) => (
                                                <SelectItem key={material} value={material}>
                                                    {material}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>

                <div className="flex-1 p-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                            <Label className="label font-bold">BCT (Top Load)</Label>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Enter the Box Compression Test value</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <div className="flex gap-2">
                            <FormField
                                control={form.control}
                                name="tertiary_bct"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormControl>
                                            <Input type="number" placeholder="Enter value" {...field} className="bg-white" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Select
                                value={form.watch('tertiary_bct_unit')}
                                onValueChange={(value) => form.setValue('tertiary_bct_unit', value)}
                            >
                                <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                    <SelectValue defaultValue="lbf" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="lbf">lbf</SelectItem>
                                    {/* <SelectItem value="N">N</SelectItem>
                                    <SelectItem value="kgf">kgf</SelectItem> */}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                            <Label className="label font-bold">Length*</Label>
                            <div className="flex items-center gap-1">
                                {!form.getValues('isTertiaryLengthAutoCalculated') && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    title="Reset to auto-calculated value"
                                                    onClick={() => {
                                                        const calculatedValue = calculateTertiaryDimension('length');
                                                        const unit = form.getValues('tertiary_length_unit') || 'in';
                                                        const convertedValue = convertLength(calculatedValue, 'in', unit);
                                                        form.setValue('tertiary_length', safeToFixed(convertedValue), {
                                                            shouldValidate: true,
                                                            shouldDirty: true
                                                        });
                                                        form.setValue('isTertiaryLengthAutoCalculated',true);
                                                    }}
                                                    className="p-1 hover:bg-gray-100 rounded-full"
                                                >
                                                    <RotateCcw className="h-3 w-3 text-gray-500" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Reset to auto-calculated value</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Formula: Rows in Pack/Case * Secondary Case Length(in) + Tertiary Package factor</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <FormField
                                control={form.control}
                                name="tertiary_length"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="Enter value"
                                                {...field}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    form.setValue('isTertiaryLengthAutoCalculated',false);
                                                }}
                                                className={` border-required border-2 ${form.getValues('isTertiaryLengthAutoCalculated') ? 'bg-bgYellow' : ''}`}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Select
                                value={form.watch('tertiary_length_unit')}
                                onValueChange={(value) => {
                                    const currentValue = safeNumberConvert(form.getValues('tertiary_length'));
                                    const currentUnit = form.getValues('tertiary_length_unit');
                                    const convertedValue = convertLength(currentValue, currentUnit || 'in', value);
                                    form.setValue('tertiary_length', safeToFixed(convertedValue), {
                                        shouldValidate: true,
                                        shouldDirty: true
                                    });
                                    form.setValue('tertiary_length_unit', value);
                                }}
                            >
                                <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                    <SelectValue defaultValue="in" />
                                </SelectTrigger>
                                <SelectContent>
                                    {inUnits.map((unit) => (
                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                            <Label className="label font-bold">Width*</Label>
                            <div className="flex items-center gap-1">
                                {!form.getValues('isTertiaryWidthAutoCalculated') && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    title="Reset to auto-calculated value"
                                                    onClick={() => {
                                                        const calculatedValue = calculateTertiaryDimension('width');
                                                        const unit = form.getValues('tertiary_width_unit') || 'in';
                                                        const convertedValue = convertLength(calculatedValue, 'in', unit);
                                                        form.setValue('tertiary_width', safeToFixed(convertedValue), {
                                                            shouldValidate: true,
                                                            shouldDirty: true
                                                        });
                                                        form.setValue('isTertiaryWidthAutoCalculated',true);
                                                    }}
                                                    className="p-1 hover:bg-gray-100 rounded-full"
                                                >
                                                    <RotateCcw className="h-3 w-3 text-gray-500" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Reset to auto-calculated value</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Formula: Columns in Pack/Case * Secondary Case Width(in) + Tertiary Package factor</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <FormField
                                control={form.control}
                                name="tertiary_width"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="Enter value"
                                                {...field}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    form.setValue('isTertiaryWidthAutoCalculated',false);
                                                }}
                                                className={` border-required border-2 ${form.getValues('isTertiaryWidthAutoCalculated') ? 'bg-bgYellow' : ''}`}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Select
                                value={form.watch('tertiary_width_unit')}
                                onValueChange={(value) => {
                                    const currentValue = safeNumberConvert(form.getValues('tertiary_width'));
                                    const currentUnit = form.getValues('tertiary_width_unit');
                                    const convertedValue = convertLength(currentValue, currentUnit || 'in', value);
                                    form.setValue('tertiary_width', safeToFixed(convertedValue), {
                                        shouldValidate: true,
                                        shouldDirty: true
                                    });
                                    form.setValue('tertiary_width_unit', value);
                                }}
                            >
                                <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                    <SelectValue defaultValue="in" />
                                </SelectTrigger>
                                <SelectContent>
                                    {inUnits.map((unit) => (
                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Second Row */}
            <div className="flex w-full border-x border-b">
                <div className="flex-1 p-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                            <Label className="label font-bold">Height*</Label>
                            <div className="flex items-center gap-1">
                                {!form.getValues('isTertiaryHeightAutoCalculated') && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    title="Reset to auto-calculated value"
                                                    onClick={() => {
                                                        const calculatedValue = calculateTertiaryDimension('height');
                                                        const unit = form.getValues('tertiary_height_unit') || 'in';
                                                        const convertedValue = convertLength(calculatedValue, 'in', unit);
                                                        form.setValue('tertiary_height', safeToFixed(convertedValue), {
                                                            shouldValidate: true,
                                                            shouldDirty: true
                                                        });
                                                        form.setValue('isTertiaryHeightAutoCalculated',true);
                                                    }}
                                                    className="p-1 hover:bg-gray-100 rounded-full"
                                                >
                                                    <RotateCcw className="h-3 w-3 text-gray-500" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Reset to auto-calculated value</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Formula: Secondary Case Height(in) + Tertiary Package factor</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <FormField
                                control={form.control}
                                name="tertiary_height"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="Enter value"
                                                {...field}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    form.setValue('isTertiaryHeightAutoCalculated',false);
                                                }}
                                                className={` border-required border-2 ${form.getValues('isTertiaryHeightAutoCalculated') ? 'bg-bgYellow' : ''}`}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Select
                                value={form.watch('tertiary_height_unit')}
                                onValueChange={(value) => {
                                    const currentValue = safeNumberConvert(form.getValues('tertiary_height'));
                                    const currentUnit = form.getValues('tertiary_height_unit');
                                    const convertedValue = convertLength(currentValue, currentUnit || 'in', value);
                                    form.setValue('tertiary_height', safeToFixed(convertedValue), {
                                        shouldValidate: true,
                                        shouldDirty: true
                                    });
                                    form.setValue('tertiary_height_unit', value);
                                }}
                            >
                                <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                    <SelectValue defaultValue="in" />
                                </SelectTrigger>
                                <SelectContent>
                                    {inUnits.map((unit) => (
                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-4">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                            <Label className="label font-bold">Weight*</Label>
                            {!form.getValues('isTertiaryWeightAutoCalculated') && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                title="Reset to auto-calculated value"
                                                aria-label="Reset tertiary package weight to auto-calculated value"
                                                onClick={() => {
                                                    const calculatedWeight = calculateTertiaryPackageWeight(
                                                        form.getValues('secondary_weight'),
                                                        form.getValues('secondary_weight_unit') || 'lbs',
                                                        request?.pack_per_case
                                                    );
                                                    if (!calculatedWeight) return;
                                                    form.setValue('tertiary_weight', calculatedWeight, {
                                                        shouldValidate: true,
                                                        shouldDirty: true
                                                    });
                                                    form.setValue('tertiary_weight_unit', 'lbs');
                                                    form.setValue('isTertiaryWeightAutoCalculated', true);
                                                }}
                                                className="p-1 hover:bg-gray-100 rounded-full"
                                            >
                                                <RotateCcw className="h-3 w-3 text-gray-500" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Reset to auto-calculated value</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Formula: Secondary package weight(lbs) * Pack/Case + 0.22</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <div className="flex gap-2">
                            <FormField
                                control={form.control}
                                name="tertiary_weight"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="Enter value"
                                                {...field}
                                                onChange={(event) => {
                                                    field.onChange(event);
                                                    form.setValue('isTertiaryWeightAutoCalculated', false);
                                                }}
                                                className={`border-required border-2 ${form.getValues('isTertiaryWeightAutoCalculated') ? 'bg-bgYellow' : 'bg-white'}`}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Select
                                value={form.watch('tertiary_weight_unit')}
                                onValueChange={(value) => form.setValue('tertiary_weight_unit', value)}
                            >
                                <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                    <SelectValue defaultValue="g" />
                                </SelectTrigger>
                                <SelectContent>
                                    {
                                        lbsUnits.map((unit) => (
                                            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                        ))
                                    }
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <div className="flex-1 p-4"></div>
                <div className="flex-1 p-4"></div>
            </div>

            <div className="mt-4 p-4 text-sm text-gray-500">
                <p>* Required fields</p>
                <p>Cells highlighted in light yellow contain calculated values. Users can update these cells with actual values when necessary</p>
            </div>
        </div>
    );
};

export default TertiaryPackageDetails; 
